import { useState, useEffect } from 'react';

/**
 * Custom hook to detect user's country based on IP address
 * @returns {Object} - { countryCode: string, isLoading: boolean, error: string | null }
 */
export const useCountryDetection = () => {
  const [countryCode, setCountryCode] = useState('US'); // Default fallback
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const detectCountry = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Try multiple IP geolocation services for better reliability
        const services = [
          'https://ipapi.co/json/',
          'https://ip-api.com/json/',
          'https://api.country.is/'
        ];

        // Use Promise.allSettled to try all services in parallel
        const results = await Promise.allSettled(
          services.map(async (service) => {
            try {
              const response = await fetch(service, {
                method: 'GET',
                headers: {
                  'Accept': 'application/json',
                },
              });

              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }

              const data = await response.json();
              
              // Handle different response formats
              let detectedCountry = null;
              
              if (service.includes('ipapi.co')) {
                detectedCountry = data.country_code;
              } else if (service.includes('ip-api.com')) {
                detectedCountry = data.countryCode;
              } else if (service.includes('country.is')) {
                detectedCountry = data.country;
              }

              if (detectedCountry && detectedCountry.length === 2) {
                return {
                  country: detectedCountry.toUpperCase(),
                  service
                };
              }
              return null;
            } catch (serviceError) {
              console.warn(`Failed to detect country via ${service}:`, serviceError);
              return null;
            }
          })
        );

        // Find the first successful result
        const successfulResult = results.find(result => 
          result.status === 'fulfilled' && result.value !== null
        );

        if (successfulResult) {
          const { country, service } = successfulResult.value;
          setCountryCode(country);
          console.log(`Country detected: ${country} via ${service}`);
        }
      } catch (err) {
        console.error('Country detection failed:', err);
        setError(err.message);
        // Keep default 'US' country code
      } finally {
        setIsLoading(false);
      }
    };

    detectCountry();
  }, []);

  return { countryCode, isLoading, error };
};
